// Display formatting for coordinate-plane values and the canonical coordinate id.
// Purely presentational — the canonical id (see coordId.js) is never altered; we
// only derive a short, human-glanceable token from it for the specimen placard.

// The width of the whole-set framing — the "x1" a magnification is measured
// against. Imported rather than restated so this can never disagree with the view
// the generator actually opens on.
//
// The explicit .js is the same rule renderCore.js's header sets out: Webpack and
// Jest resolve either form, but Node's ESM loader requires the extension, and this
// module is now on a headless path — the print renderer names its output file with
// shortId, and Block J's og:description is a magnification. renderSize.js reads
// `window` only inside its last two functions, so importing it off-browser is fine.
import { SET_WIDTH } from './renderSize.js';

// A complex-plane value, formatted for a museum-style placard: enough precision
// to be meaningful at deep zooms, without spilling raw 17-digit float noise
// (e.g. -0.8695703125 -> "-0.86957031"). Very small magnitudes fall back to
// significant-figure notation so a deep zoom doesn't collapse to "0.00000000".
export const formatCoord = (value) => {
    if (!Number.isFinite(value)) return '—';
    const magnitude = Math.abs(value);
    if (magnitude !== 0 && magnitude < 1e-6) {
        return value.toExponential(4);
    }
    // Fixed 8dp, then trim trailing zeros (but keep at least one decimal place).
    let s = value.toFixed(8).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '.0');
    return s;
};

// How far a region is magnified relative to the view of the entire set. Purely
// presentational, and derived rather than stored: it is a second, humanly
// meaningful reading of the same bounds the placard already shows. A stranger
// cannot tell a deep zoom from a shallow one by looking, so this is what conveys
// that the image they're looking at sits ~250,000x inside the set.
//
// Above a million, grouped digits stop being readable and start being a blur, so
// it switches to short-scale words. Returns null for a region with no extent.
export const magnification = ({ realMin, realMax }) => {
    const span = realMax - realMin;
    if (!Number.isFinite(span) || span <= 0) return null;

    const factor = SET_WIDTH / span;
    if (!Number.isFinite(factor)) return null;

    if (factor < 10) return factor.toFixed(1);
    if (factor < 1e6) return Math.round(factor).toLocaleString('en-US');

    const scales = [
        [1e12, 'trillion'],
        [1e9, 'billion'],
        [1e6, 'million']
    ];
    for (const [size, word] of scales) {
        if (factor >= size && factor < size * 1000) {
            const value = factor / size;
            // 8.4 million, but 840 million — a decimal on a 3-digit mantissa is noise.
            return `${value >= 100 ? Math.round(value) : value.toFixed(1)} ${word}`;
        }
    }
    return factor.toExponential(1);
};

// Deterministic short token derived from the canonical id, for at-a-glance
// identification on the placard/filmstrip. FNV-1a -> 6 hex chars. This is a
// display convenience, NOT the identity: uniqueness is still enforced on the
// full canonical id from coordId.js.
export const shortId = (id) => {
    let hash = 0x811c9dc5;
    for (let i = 0; i < id.length; i++) {
        hash ^= id.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(16).padStart(8, '0').slice(0, 6);
};
